<template>
	<ActionItemWrapper
		v-jest="'action-wrapper'"
		:action-item="actionItem"
		:actions="filteredActions"
		:is-action-pending="isActionPending"
		:is-currently-inspecting="isCurrentlyInspecting"
		@handle-inspect="handleInspect"
		@handle-uninspect="handleUninspect"
		@handle-false-alarm="handleFalseAlarm"
	>
		<template #flagged-message>
			<span v-jest="'flagged-message'">
				<span
					v-if="!itemContentMessage.hasKeywords"
					v-jest="'unformatted-comment'"
				>
					{{ actionItem.content.data.comment }}
				</span>
				<span
					v-if="itemContentMessage.hasKeyword"
					v-jest="'keyword-comment'"
					class="tw-block"
				>
					{{ itemContentMessage.message[0] }} <span class="tw-font-bold">{{ itemContentMessage.message[1] }}</span> {{ itemContentMessage.message[2] }}
				</span>
				<span
					v-if="itemContentMessage.hasEmail"
					v-jest="'email-comment'"
					class="tw-block"
				>
					{{ itemContentMessage.emailMessage[0] }} <span class="tw-font-bold">{{ itemContentMessage.emailMessage[1] }}</span>
				</span>
				<span
					v-if="itemContentMessage.hasPhone"
					v-jest="'phone-comment'"
					class="tw-block"
				>
					{{ itemContentMessage.phoneMessage[0] }} <span class="tw-font-bold">{{ itemContentMessage.phoneMessage[1] }}</span>
				</span>
			</span>
		</template>
		<template #flagged-user-info>
			<span
				v-jest="'role'"
				class="tw-font-bold"
			>
				{{ role }}:
			</span>
			<span v-jest="'sender-name'">{{ name }}</span>
			<RouterLink
				v-jest="'action-item-link'"
				:to="itemLinkPath"
				class="tw-mx-2 tw-text-blue-regular tw-underline tw-font-bold"
			>
				{{ itemLinkPathText }}
			</RouterLink>
			{{ convertedDate }}
		</template>
		<template #status-text>
			<span v-jest="'status-text'">
				{{ statusText }}
			</span>
		</template>
	</ActionItemWrapper>
</template>

<script>
import { mapState } from "vuex";

import { getStatusText, RESOLVE_ACTION_ID, HIDE_ACTION_ID, ESCALATE_ACTION_ID, formatMessageArray, formatMessage, formatEmailMessage, formatPhoneMessage } from "@/modules/ActionCenter/index.js";

import { formatDateToTimestampYMD, formatTimestampToHM } from "../../../utilities/dateHelpers.js";
import { checkIsObjectKeysPresentAndRightType } from "../../../utilities/propValidationHelpers.js";
import { getErrorText, showErrorModal } from "../../../utilities/errorHandlingHelpers.js";

import ActionItemWrapper from "./ActionItemWrapper.vue";

const requiredKeys = [
	{ property: "action_type_id", type: ["number", null] },
	{ property: "actionable_id", type: ["number"] },
	{ property: "created_at", type: ["number"] },
	{ property: "content", type: ["object"], subKeys: [
		{ property: "data", type: ["object"], subKeys: [
			{ property: "essay_id", type: ["number"] },
			{ property: "tutor_id", type: ["number"] },
			{ property: "essay_url", type: ["string"] },
			{ property: "comment", type: ["string"] },
			{ property: "message_formatted", type: ["string"] },
			{ property: "tutor", type: ["object"], subKeys: [
				{ property: "user_id", type: ["number"] },
				{ property: "name", type: ["string"] },
			] },
		] },
	] },
	{ property: "picked_up_user_id", type: ["number", null] },
	{ property: "picked_up_at", type: ["number", null] },
	{ property: "resolved_at", type: ["number", null] },
];

export default {
	components: {
		ActionItemWrapper,
	},
	props: {
		actionItem: {
			type: Object,
			required: true,
			validator: (item) => checkIsObjectKeysPresentAndRightType(item, requiredKeys),
		},
	},
	data() {
		return {
			actions: [
				{ id: RESOLVE_ACTION_ID, name: `Resolve`, icon: "check-outlined", handleEvent: this.handleResolve },
				{
					id: HIDE_ACTION_ID,
					name: `Hide`,
					icon: "hide",
					color: "tw-text-origami-black",
					handleEvent: this.handleHide,
				},
			],
			isCurrentlyInspecting: false,
			isActionPending: false,
		};
	},
	computed: {
		...mapState(["currentUser", "PlatformManager"]),
		itemContentMessage() {
			const messageArray = formatMessageArray(this.actionItem.content.data.message_formatted);

			const messageContent = {
				hasEmail: messageArray.some((str) => str.includes("The following emails were shared in the classroom: ")),
				hasKeyword: messageArray.some((str) => str.includes("Message: ")),
				hasPhone: messageArray.some((str) => str.includes("The following phone numbers were shared in the classroom: ")),
				message: formatMessage(messageArray),
				phoneMessage: formatPhoneMessage(messageArray),
				emailMessage: formatEmailMessage(messageArray),
			};
			messageContent.hasKeywords = messageContent.hasEmail ||
				messageContent.hasKeyword ||
				messageContent.hasPhone;

			return messageContent;
		},
		itemLinkPathText() {
			return `Essay ID #${this.actionItem.content.data.essay_id}`;
		},
		convertedDate() {
			return `Submitted ${formatDateToTimestampYMD(this.actionItem.created_at)} ${formatTimestampToHM(this.actionItem.created_at)}`;
		},
		itemLinkPath() {
			return `/${this.currentUser.role}/activity?essayId=[${this.actionItem.content.data.essay_id}]&tab=Essays`;
		},
		statusText() {
			return getStatusText(this.actionItem, this.isPickedUpByCurrentUser);
		},
		role() {
			return "Tutor";
		},
		name() {
			return this.actionItem.content.data.tutor.name;
		},
		isPickedUpByCurrentUser() {
			return this.actionItem.picked_up_user_id === this.currentUser.id;
		},
		filteredActions() {
			return this.actions.filter((action) => action.id !== (this.isPickedUpByCurrentUser ? 0 : -1));
		},
	},
	async created() {
		this.actions.sort((segmentA, segmentB) => segmentA.id - segmentB.id);
		this.isCurrentlyInspecting = this.isPickedUpByCurrentUser;
	},
	methods: {
		async handleInspect() {
			this.isActionPending = true;
			const payload = {
				id: this.actionItem.id,
				params: {
					picked_up_user_id: this.currentUser.id,
				},
			};
			try {
				await this.$store.dispatch("PlatformManager/ActionCenter/updateActionItem", payload);
				this.isCurrentlyInspecting = true;
			} catch (error) {
				const message = getErrorText("updating the action item");
				showErrorModal(this, {
					message,
				});
			} finally {
				this.isActionPending = false;
			}
		},
		async handleUninspect() {
			this.isActionPending = true;
			const payload = {
				id: this.actionItem.id,
				params: {
					unassign: true,
				},
			};
			try {
				await this.$store.dispatch("PlatformManager/ActionCenter/updateActionItem", payload);
				this.isCurrentlyInspecting = false;
			} catch (error) {
				const message = getErrorText("uninspecting the action item");
				showErrorModal(this, {
					message,
				});
			} finally {
				this.isActionPending = false;
			}
		},
		async handleEscalate() {
			this.isActionPending = true;
			const payload = {
				id: this.actionItem.id,
				params: {
					action_type_id: ESCALATE_ACTION_ID,
					picked_up_user_id: this.currentUser.id,
				},
			};
			try {
				await this.$store.dispatch("PlatformManager/ActionCenter/updateActionItem", payload);
			} catch (error) {
				const message = getErrorText("updating the action item");
				showErrorModal(this, {
					message,
				});
			} finally {
				this.isActionPending = false;
			}
		},
		handleFalseAlarm() {
			this.$store.commit("PlatformManager/ActionCenter/SELECT_ACTION_ITEM", this.actionItem);
			this.$store.commit("PlatformManager/ActionCenter/OPEN_FALSE_ALARM_DIALOG");
		},
		handleResolve() {
			this.$store.commit("PlatformManager/ActionCenter/SELECT_ACTION_ITEM", this.actionItem);
			this.$store.commit("PlatformManager/ActionCenter/OPEN_RESOLVE_DIALOG");
		},
		handleHide() {
			this.$store.commit("PlatformManager/ActionCenter/SELECT_ACTION_ITEM", this.actionItem);
			this.$store.commit("PlatformManager/ActionCenter/OPEN_HIDE_DIALOG");
		},
	},
};
</script>

<style scoped>
.action__dropdown :deep(li) {
	padding: 0;
}
</style>
